import { useState } from 'react';
import { Card, FormGroup, InputWithUnit } from '../ui/Card';
import type { CreditScoreRange, CoBorrower } from '../../lib/types';

export interface ProfileFormData {
  name: string;
  cashOnHand: number;
  defaultDownPaymentPct: number;
  grossAnnualIncome: number;
  monthlyDebts: number;
  creditScoreRange: CreditScoreRange;
  use401k: boolean;
  loan401kAmount: number;
  loan401kRate: number;
  loan401kTerm: number;
  coBorrower: CoBorrower | null;
}

export const CREDIT_SCORE_OPTIONS: { value: CreditScoreRange; label: string }[] = [
  { value: '760', label: '760+' },
  { value: '740', label: '740–759' },
  { value: '720', label: '720–739' },
  { value: '700', label: '700–719' },
  { value: '680', label: '680–699' },
  { value: '660', label: '660–679' },
  { value: '640', label: '640–659' },
];

const LOAN_TERM_OPTIONS = [
  { value: 3, label: '3 years' },
  { value: 4, label: '4 years' },
  { value: 5, label: '5 years' },
];

interface ProfileFormProps {
  initialValues?: Partial<ProfileFormData>;
  onSubmit: (data: ProfileFormData) => void;
  onCancel: () => void;
  submitLabel?: string;
  title?: string;
}

export function ProfileForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  title = 'Buyer Profile',
}: ProfileFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [cashOnHand, setCashOnHand] = useState(initialValues?.cashOnHand ?? 50000);
  const [defaultDownPaymentPct, setDefaultDownPaymentPct] = useState(initialValues?.defaultDownPaymentPct ?? 10);
  const [grossAnnualIncome, setGrossAnnualIncome] = useState(initialValues?.grossAnnualIncome ?? 85000);
  const [monthlyDebts, setMonthlyDebts] = useState(initialValues?.monthlyDebts ?? 0);
  const [creditScoreRange, setCreditScoreRange] = useState<CreditScoreRange>(
    initialValues?.creditScoreRange ?? '720'
  );

  // 401(k)
  const [use401k, setUse401k] = useState(initialValues?.use401k ?? false);
  const [loan401kAmount, setLoan401kAmount] = useState(initialValues?.loan401kAmount ?? 25000);
  const [loan401kRate, setLoan401kRate] = useState(initialValues?.loan401kRate ?? 5.5);
  const [loan401kTerm, setLoan401kTerm] = useState(initialValues?.loan401kTerm ?? 5);

  // Co-borrower
  const [hasCoBorrower, setHasCoBorrower] = useState(!!(initialValues?.coBorrower));
  const [coBorrowerName, setCoBorrowerName] = useState(initialValues?.coBorrower?.name ?? '');
  const [coBorrowerIncome, setCoBorrowerIncome] = useState(initialValues?.coBorrower?.grossAnnualIncome ?? 0);
  const [coBorrowerDebts, setCoBorrowerDebts] = useState(initialValues?.coBorrower?.monthlyDebts ?? 0);
  const [coBorrowerCredit, setCoBorrowerCredit] = useState<CreditScoreRange>(
    initialValues?.coBorrower?.creditScoreRange ?? '720'
  );

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      cashOnHand,
      defaultDownPaymentPct,
      grossAnnualIncome,
      monthlyDebts,
      creditScoreRange,
      use401k,
      loan401kAmount,
      loan401kRate,
      loan401kTerm,
      coBorrower: hasCoBorrower
        ? { name: coBorrowerName.trim(), grossAnnualIncome: coBorrowerIncome, monthlyDebts: coBorrowerDebts, creditScoreRange: coBorrowerCredit }
        : null,
    });
  };

  const sectionDividerStyle = {
    borderTop: '1px solid var(--border)',
    marginTop: '1rem',
    paddingTop: '1rem',
  };

  const toggleLabelStyle = {
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '0.5rem',
    cursor: 'pointer',
    userSelect: 'none' as const,
  };

  return (
    <Card title={title}>
      {/* Primary borrower */}
      <FormGroup label="Profile Name">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Solo Buyer, With Partner, With 401k"
        />
      </FormGroup>
      <div className="form-row">
        <FormGroup label="Cash on Hand">
          <InputWithUnit unit="$" value={cashOnHand} onChange={setCashOnHand} min={0} step={5000} />
        </FormGroup>
        <FormGroup label="Default Down Payment">
          <InputWithUnit unit="%" unitPosition="right" value={defaultDownPaymentPct} onChange={setDefaultDownPaymentPct} min={0} max={100} step={0.5} />
        </FormGroup>
        <FormGroup label="Gross Annual Income">
          <InputWithUnit unit="$" value={grossAnnualIncome} onChange={setGrossAnnualIncome} min={0} step={5000} />
        </FormGroup>
      </div>
      <div className="form-row">
        <FormGroup label="Monthly Debts (car, student, credit)">
          <InputWithUnit unit="$" value={monthlyDebts} onChange={setMonthlyDebts} min={0} step={50} />
        </FormGroup>
        <FormGroup label="Credit Score Range">
          <select value={creditScoreRange} onChange={e => setCreditScoreRange(e.target.value as CreditScoreRange)}>
            {CREDIT_SCORE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </FormGroup>
      </div>

      {/* Co-Borrower Section */}
      <div style={sectionDividerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: hasCoBorrower ? '0.75rem' : 0 }}>
          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={hasCoBorrower}
              onChange={e => setHasCoBorrower(e.target.checked)}
              style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Co-Borrower</span>
          </label>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Spouse, partner, or joint applicant — incomes and debts are combined
          </span>
        </div>

        {hasCoBorrower && (
          <div>
            <FormGroup label="Co-Borrower Name">
              <input
                type="text"
                value={coBorrowerName}
                onChange={e => setCoBorrowerName(e.target.value)}
                placeholder="e.g. Jane Smith"
              />
            </FormGroup>
            <div className="form-row">
              <FormGroup label="Gross Annual Income">
                <InputWithUnit unit="$" value={coBorrowerIncome} onChange={setCoBorrowerIncome} min={0} step={5000} />
              </FormGroup>
              <FormGroup label="Monthly Debts">
                <InputWithUnit unit="$" value={coBorrowerDebts} onChange={setCoBorrowerDebts} min={0} step={50} />
              </FormGroup>
              <FormGroup label="Credit Score Range">
                <select value={coBorrowerCredit} onChange={e => setCoBorrowerCredit(e.target.value as CreditScoreRange)}>
                  {CREDIT_SCORE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FormGroup>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
              Combined income and debts are used for DTI calculations. The lower credit score is used for rate qualification.
            </p>
          </div>
        )}
      </div>

      {/* 401(k) Loan Section */}
      <div style={sectionDividerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: use401k ? '0.75rem' : 0 }}>
          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={use401k}
              onChange={e => setUse401k(e.target.checked)}
              style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>401(k) Loan</span>
          </label>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Borrow from retirement to boost down payment
          </span>
        </div>

        {use401k && (
          <div>
            <div className="form-row">
              <FormGroup label="Loan Amount">
                <InputWithUnit unit="$" value={loan401kAmount} onChange={setLoan401kAmount} min={0} step={1000} />
              </FormGroup>
              <FormGroup label="Interest Rate">
                <InputWithUnit unit="%" unitPosition="right" value={loan401kRate} onChange={setLoan401kRate} min={0} max={15} step={0.25} />
              </FormGroup>
              <FormGroup label="Repayment Term">
                <select value={loan401kTerm} onChange={e => setLoan401kTerm(Number(e.target.value))}>
                  {LOAN_TERM_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FormGroup>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
              Loan proceeds reduce cash to close. Monthly repayment is added to your debt obligations. Max $50,000 or 50% of vested balance.
            </p>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" disabled={!name.trim()} onClick={handleSubmit}>
          {submitLabel}
        </button>
      </div>
    </Card>
  );
}
